import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Gavel, Trophy, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatCurrency, currencySymbol } from "@/lib/currency";

/**
 * CloseFreightDialog — per-shipment award picker for freight auctions.
 *
 * Privacy model matches BOP: bidders appear only as B1/B2/... anonymous
 * labels. Real identities are resolved server-side once the creator commits.
 *
 * Coverage guard: if any shipment has no eligible bid (or every quote has
 * ETD earlier than the shipment's container-pickup date) the close is blocked
 * — the creator must extend the deadline or invite more bidders.
 */
export const CloseFreightDialog = ({ auctionId, onChange }) => {
  const [open, setOpen] = useState(false);
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(false);
  const [awards, setAwards] = useState({}); // {shipment_ref: label}
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/auctions/${auctionId}/freight-allocation-matrix`);
      setMatrix(data);
      // Pre-select the recommended (cheapest-eligible) label per shipment
      setAwards({ ...(data.recommended_award || {}) });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load allocation matrix");
    } finally { setLoading(false); }
  }, [auctionId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const uncovered = useMemo(() => {
    if (!matrix) return [];
    return (matrix.shipments || [])
      .filter((s) => !matrix.shipment_lowest?.[s.shipment_ref])
      .map((s) => s.shipment_ref);
  }, [matrix]);

  const allAwarded = useMemo(() => {
    if (!matrix) return false;
    if (uncovered.length > 0) return false;
    return (matrix.shipments || []).every((s) => awards[s.shipment_ref]);
  }, [matrix, awards, uncovered]);

  const projectedTotal = useMemo(() => {
    if (!matrix) return 0;
    let t = 0;
    for (const s of matrix.shipments || []) {
      const lbl = awards[s.shipment_ref];
      if (!lbl) continue;
      const cell = matrix.cells?.[s.shipment_ref]?.[lbl];
      if (cell?.rate != null) t += cell.rate;
    }
    return Math.round(t * 100) / 100;
  }, [matrix, awards]);

  const awardCounts = useMemo(() => {
    const c = {};
    for (const s of matrix?.shipments || []) {
      const lbl = awards[s.shipment_ref];
      if (lbl) c[lbl] = (c[lbl] || 0) + 1;
    }
    return c;
  }, [matrix, awards]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/auctions/${auctionId}/close`, {
        declare_winner: true,
        shipment_awards: awards,
      });
      toast.success("Closed — containers allocated across bidders");
      setOpen(false);
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const sym = matrix ? currencySymbol(matrix.currency) : "";
  const canClose = matrix && matrix.bidder_labels.length > 0 && uncovered.length === 0 && allAwarded;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="w-full rounded-none border-black h-11 btn-sharp"
          data-testid="open-close-freight-dialog"
        >
          <Gavel size={14} weight="bold" className="mr-2" /> Close & Allocate
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none max-w-6xl" data-testid="close-freight-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-extrabold text-2xl tracking-tightest">
            Close Freight Auction — Allocate Containers
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-neutral-700 leading-relaxed">
          Each container is awarded to a single bidder. Bidders appear as anonymous labels (<strong>B1, B2, …</strong>) — real
          identities are resolved server-side after commit. The <strong>cheapest eligible</strong> quote (ETD ≥ container-pickup) is
          pre-selected per shipment; you can override any row.
        </p>

        {loading ? (
          <div className="py-8 text-xs tracking-[0.3em] uppercase text-neutral-500">Loading allocation matrix...</div>
        ) : !matrix ? null : matrix.bidder_labels.length === 0 ? (
          <div className="border border-dashed border-black/15 p-6 text-center text-sm text-neutral-600">
            No bids placed yet — there is nothing to allocate.
          </div>
        ) : (
          <>
            {uncovered.length > 0 && (
              <div className="border-l-2 border-[#B91C1C] bg-red-50 p-3 text-sm text-[#7F1D1D]" data-testid="uncovered-warning">
                <div className="font-semibold flex items-center gap-1.5">
                  <Warning size={14} weight="fill" /> Cannot close yet
                </div>
                <div className="mt-1 text-xs">
                  These shipments have no eligible bid (missing quote, or every ETD is before the container-pickup date):{" "}
                  <span className="font-mono-plex">{uncovered.join(", ")}</span>. Extend the deadline or invite more bidders.
                </div>
              </div>
            )}
            <div className="overflow-x-auto border border-black/10 mt-2">
              <table className="w-full text-sm" data-testid="allocation-matrix-table">
                <thead className="bg-[#F8F9FA] border-b border-black/10">
                  <tr className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 font-semibold">
                    <th className="text-left p-3">Shipment</th>
                    <th className="text-left p-3">Container Pickup</th>
                    {matrix.bidder_labels.map((lbl) => (
                      <th key={lbl} className="text-right p-3" data-testid={`matrix-col-${lbl}`}>{lbl}</th>
                    ))}
                    <th className="text-center p-3">Award To</th>
                  </tr>
                </thead>
                <tbody>
                  {(matrix.shipments || []).map((s) => {
                    const row = matrix.cells?.[s.shipment_ref] || {};
                    const lowest = matrix.shipment_lowest?.[s.shipment_ref];
                    return (
                      <tr key={s.shipment_ref} className="border-b border-black/5" data-testid={`allocation-row-${s.shipment_ref}`}>
                        <td className="p-3">
                          <div className="font-mono-plex font-semibold text-sm">{s.shipment_ref}</div>
                        </td>
                        <td className="p-3 font-mono-plex text-xs text-neutral-600">{s.cargo_ready_date}</td>
                        {matrix.bidder_labels.map((lbl) => {
                          const cell = row[lbl];
                          const isLowest = lowest?.label === lbl;
                          const etdFail = cell && s.cargo_ready_date && cell.etd_committed && cell.etd_committed < s.cargo_ready_date;
                          return (
                            <td key={lbl} className={`p-3 text-right font-mono-plex text-xs ${isLowest ? "klein-blue font-semibold" : ""} ${etdFail ? "text-neutral-300" : ""}`}>
                              {cell ? (
                                <>
                                  <div>{sym}{Number(cell.rate).toLocaleString()}</div>
                                  <div className="text-[10px] text-neutral-500">
                                    {cell.vessel_name} · ETD {cell.etd_committed}
                                  </div>
                                  {etdFail && (
                                    <div className="text-[10px] text-red-600">date fail</div>
                                  )}
                                </>
                              ) : (
                                <span className="text-neutral-400">—</span>
                              )}
                            </td>
                          );
                        })}
                        <td className="p-3 text-center">
                          <select
                            value={awards[s.shipment_ref] || ""}
                            onChange={(e) => setAwards({ ...awards, [s.shipment_ref]: e.target.value })}
                            className="rounded-none border border-black/30 h-9 px-2 text-sm font-mono-plex"
                            data-testid={`award-select-${s.shipment_ref}`}
                          >
                            <option value="">—</option>
                            {matrix.bidder_labels
                              .filter((lbl) => {
                                const cell = row[lbl];
                                if (!cell) return false;
                                // exclude ETD-failing quotes from award options
                                if (s.cargo_ready_date && cell.etd_committed && cell.etd_committed < s.cargo_ready_date) return false;
                                return true;
                              })
                              .map((lbl) => (
                                <option key={lbl} value={lbl}>{lbl}</option>
                              ))}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="border-t-2 border-black bg-[#F8F9FA] font-semibold">
                    <td colSpan={2} className="p-3 text-right text-[10px] tracking-[0.25em] uppercase text-neutral-500">
                      Containers awarded / bidder
                    </td>
                    {matrix.bidder_labels.map((lbl) => (
                      <td key={lbl} className="p-3 text-right font-mono-plex text-xs">
                        {awardCounts[lbl] || 0}
                      </td>
                    ))}
                    <td className="p-3 text-center text-[10px] tracking-[0.25em] uppercase text-neutral-500">
                      Total: <span className="klein-blue font-mono-plex">{formatCurrency(projectedTotal, matrix.currency)}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-neutral-500 leading-relaxed">
              You&apos;re splitting {matrix.shipments.length} container{matrix.shipments.length === 1 ? "" : "s"} across up to{" "}
              {Object.keys(awardCounts).length} vendor(s). Each winning bidder will be emailed with the shipments they were awarded,
              including vessel + ETD + rate.
            </p>
          </>
        )}

        <DialogFooter className="gap-2 sm:gap-2 flex-col sm:flex-row">
          <Button
            onClick={submit}
            disabled={busy || !canClose}
            className="rounded-none bg-[#22D3EE] text-[#0B1929] font-semibold h-11 btn-sharp"
            data-testid="close-freight-commit"
          >
            <Trophy size={14} weight="bold" className="mr-2" />
            {busy ? "Allocating..." : "Commit allocation & close"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
